/**
 * The arithmetic of the check view, defined once so the table, the sticky progress bar and the
 * booking confirmation cannot disagree.
 *
 * The received quantity is held as the raw input string, not as a number, because the three
 * states the spec distinguishes are exactly the three states of a text field: empty means "not
 * yet checked" and blocks booking, `0` is a legitimate value meaning "did not arrive", and
 * anything else is a quantity.
 *
 * Decimals (CHG-2026-09-stock-usage-and-price-twins, D9) apply only to a line whose article has
 * "Teilmengen zählen" (`fractional`): it takes up to 3 decimals with the de-AT comma, and its sums
 * run in integer thousandths. Every other line keeps the whole-number rules below unchanged.
 */
import type { DeliveryLine } from '@/api/endpoints/inventory-deliveries'
import type { NoStockReason } from '@/shared/lib/stock-kind'
import {
  addStockQuantities,
  formatStockDisplay,
  parseEuroToCents,
  parseStockAmount,
  subtractStockQuantities,
} from '@/shared/lib/stock-quantity'

export interface CheckLine {
  sku: string
  name: string | null
  expectedQty: number
  /** Raw input; '' means not yet checked. */
  received: string
  note: string
  /** "Teilmengen zählen": the received quantity may carry up to 3 decimals. */
  fractional?: boolean
  /** The article's base unit as shown ("Stk" by default). */
  unit?: string
  /**
   * The product holds no stock of its own (D11): the line stays on the document as the record,
   * but booking books no stock for it.
   */
  noStock?: NoStockReason
  /**
   * What the supplier's delivery note said about the delivered package unit and its net purchase
   * price (CHG-2026-09-webbill-delivery-import). All null on a hand-announced line.
   *
   * Descriptive only: `expectedQty` and `received` stay in BASE units (Stück), because a shortage
   * is counted in pieces, not in fractions of a Karton. `contentPerUnit` is what the server froze
   * at import time, so these figures never drift when the article's master data is corrected.
   */
  unitLevel?: number | null
  unitName?: string | null
  unitCount?: number | null
  contentPerUnit?: number | null
  /** Net price per DELIVERED unit (per Karton, not per Stück). */
  unitCostCents?: number | null
  /**
   * The editable net price per PIECE of a position on a hand-announced delivery (task 9.5), as
   * the raw input ("2,70"); '' means no price. Undefined on a line whose price is not editable —
   * an imported position, or any read-only view — which then reads `unitCostCents` instead.
   */
  price?: string
}

/**
 * The server's lines as the check view reads them. The line's own data wins
 * (CHG-2026-09-stock-usage-and-price-twins, phase B4): a line is fractional when it says
 * `fractionalCounting: true` or already carries a decimal — a decimal is never put into a
 * whole-number field. A line from a backend that predates the field leaves `fractional` unset, and
 * only such a line is looked up. Whole quantities become exactly the text they always were.
 */
export function checkLinesFromDelivery(
  lines: Array<DeliveryLine>,
): Array<CheckLine> {
  return lines.map((l) => {
    const carriesDecimal =
      !Number.isInteger(l.expectedQty) ||
      (l.receivedQty != null && !Number.isInteger(l.receivedQty))
    const fractional =
      l.fractionalCounting === true || carriesDecimal
        ? true
        : l.fractionalCounting === false
          ? false
          : undefined
    return {
      sku: l.sku,
      name: l.name,
      expectedQty: l.expectedQty,
      received: l.receivedQty == null ? '' : formatStockDisplay(l.receivedQty),
      note: l.note ?? '',
      ...(l.unit ? { unit: l.unit } : {}),
      ...(l.noStock ? { noStock: l.noStock } : {}),
      ...(fractional === undefined ? {} : { fractional }),
      ...deliveredUnit(l),
    }
  })
}

/**
 * The delivered package unit and purchase price of an imported line
 * (CHG-2026-09-webbill-delivery-import); nothing for a hand-announced line, whose fields are all
 * null, so such a line reads exactly as before.
 */
function deliveredUnit(l: DeliveryLine): Partial<CheckLine> {
  if (
    l.unitLevel == null &&
    l.unitName == null &&
    l.unitCount == null &&
    l.contentPerUnit == null &&
    l.unitCostCents == null
  ) {
    return {}
  }
  return {
    unitLevel: l.unitLevel,
    unitName: l.unitName,
    unitCount: l.unitCount,
    contentPerUnit: l.contentPerUnit,
    unitCostCents: l.unitCostCents,
  }
}

/**
 * The received quantity, or null when the field is empty or not a non-negative integer — or, for
 * a `fractional` line, not a non-negative quantity with at most 3 decimals ("0,5", "2.25").
 */
export function parseReceived(
  value: string,
  fractional = false,
): number | null {
  const trimmed = value.trim()
  if (trimmed === '') return null
  if (fractional) {
    const parsed = parseStockAmount(trimmed)
    return parsed.ok ? parsed.value : null
  }
  if (!/^\d+$/.test(trimmed)) return null
  return Number(trimmed)
}

/** Received minus announced, or null while the line is unchecked. */
export function lineDeviation(line: CheckLine): number | null {
  const received = parseReceived(line.received, line.fractional)
  if (received == null) return null
  return line.fractional
    ? subtractStockQuantities(received, line.expectedQty)
    : received - line.expectedQty
}

/** '±0', '+12', '−5', '+0,5' — the real minus sign, matching the count detail. */
export function formatDeviation(diff: number | null): string {
  if (diff == null) return '–'
  if (diff === 0) return '±0'
  if (diff > 0) return `+${formatStockDisplay(diff)}`
  return `−${formatStockDisplay(Math.abs(diff))}`
}

/** Surplus reads as a warning, a shortfall as a problem; an exact match stays quiet. */
export function deviationClass(diff: number | null): string {
  if (diff == null || diff === 0) return 'text-muted-foreground'
  if (diff > 0) return 'font-medium text-amber-600 dark:text-amber-400'
  return 'font-medium text-destructive'
}

/** How many lines carry a usable received quantity. */
export function checkProgress(lines: Array<CheckLine>): {
  done: number
  total: number
} {
  return {
    done: lines.filter((l) => parseReceived(l.received, l.fractional) != null)
      .length,
    total: lines.length,
  }
}

/**
 * Booking needs at least one line and a received quantity on every one of them. A price is
 * optional, but one that was typed must be readable — it is never silently dropped.
 */
export function isBookable(lines: Array<CheckLine>): boolean {
  if (lines.length === 0) return false
  return lines.every(
    (l) =>
      parseReceived(l.received, l.fractional) != null &&
      (l.price === undefined || parsePriceInput(l.price).ok),
  )
}

/**
 * "Alle wie angekündigt übernehmen": fills only the fields nobody has touched, so a quantity a
 * staff member already corrected is never overwritten by the announced one.
 */
export function confirmAll(lines: Array<CheckLine>): Array<CheckLine> {
  return lines.map((l) =>
    parseReceived(l.received, l.fractional) == null
      ? { ...l, received: formatStockDisplay(l.expectedQty) }
      : l,
  )
}

/** What the booking confirmation dialog states before the single write. */
export function bookSummary(lines: Array<CheckLine>): {
  lineCount: number
  units: number
  deviating: number
} {
  let units = 0
  let deviating = 0
  for (const line of lines) {
    const received = parseReceived(line.received, line.fractional)
    if (received == null) continue
    units = addStockQuantities(units, received)
    if (received !== line.expectedQty) deviating += 1
  }
  return { lineCount: lines.length, units, deviating }
}

/**
 * The book request body's lines; unchecked lines cannot occur because booking is gated.
 *
 * A line with an editable price (a hand-announced delivery) sends it — the book request is the
 * whole document, so an emptied field books the position without a price. An imported position
 * never sends one: its price is the Lieferschein's and the server keeps it.
 */
export function toBookLines(lines: Array<CheckLine>): Array<{
  sku: string
  receivedQty: number
  note: string | null
  unitCostCents?: number | null
}> {
  return lines.map((l) => {
    const base = {
      sku: l.sku,
      receivedQty: parseReceived(l.received, l.fractional) ?? 0,
      note: l.note.trim() === '' ? null : l.note.trim(),
    }
    if (l.price === undefined) return base
    const parsed = parsePriceInput(l.price)
    return { ...base, unitCostCents: parsed.ok ? parsed.cents : null }
  })
}

// --- Package units and purchase prices (CHG-2026-09-webbill-delivery-import) ---

/**
 * True when the import could not resolve the delivered unit and fell back to factor 1.
 *
 * The quantity is then taken at face value and may be wrong by the package factor, so the line
 * needs a human before the delivery is booked. The server also writes a marker into the line note;
 * this is the structural signal the UI marks the row with.
 */
export function hasUnitWarning(line: CheckLine): boolean {
  return line.unitCount != null && line.unitLevel == null
}

/**
 * The net purchase price of one base unit, in cents — unrounded, for display rounding only. An
 * editable price (hand-announced delivery) is read from its input.
 */
export function perPieceCents(line: CheckLine): number | null {
  if (line.price !== undefined) {
    const parsed = parsePriceInput(line.price)
    return parsed.ok ? parsed.cents : null
  }
  if (line.unitCostCents == null) return null
  const content = line.contentPerUnit ?? 1
  if (content <= 0) return null
  return line.unitCostCents / content
}

/**
 * The price of one delivered package unit ("EK / Gebinde"), only where a package unit applies —
 * on a single-piece position it would merely repeat "EK / Stk".
 */
export function perPackageCents(line: CheckLine): number | null {
  if (line.price !== undefined) return null
  if (packageCells(line).kind !== 'package') return null
  return line.unitCostCents ?? null
}

/**
 * The position's net goods value in cents: per-piece price × the quantity that counts — what was
 * received once it is entered, the announced quantity until then.
 *
 * Computed on the line total rather than per piece, so a Karton at 37,79 € for 14 pieces does not
 * lose a cent per piece to rounding. Same rule as the server's, so the figure does not change when
 * the page reloads after booking.
 */
export function lineValueCents(line: CheckLine): number | null {
  if (line.price !== undefined) {
    const parsed = parsePriceInput(line.price)
    if (!parsed.ok || parsed.cents == null) return null
    const qty =
      parseReceived(line.received, line.fractional) ?? line.expectedQty
    return Math.round(parsed.cents * qty)
  }
  if (line.unitCostCents == null) return null
  const content = line.contentPerUnit ?? 1
  if (content <= 0) return null
  const qty = parseReceived(line.received, line.fractional) ?? line.expectedQty
  return Math.round((line.unitCostCents * qty) / content)
}

/** The delivery's net goods value, or null when no position carries a purchase price. */
export function goodsValueCents(lines: Array<CheckLine>): number | null {
  let total: number | null = null
  for (const line of lines) {
    const value = lineValueCents(line)
    if (value == null) continue
    total = (total ?? 0) + value
  }
  return total
}

/** Whether the value column and the goods-value footer have anything to show. */
export function hasPrices(lines: Array<CheckLine>): boolean {
  return lines.some((l) =>
    l.price !== undefined ? perPieceCents(l) != null : l.unitCostCents != null,
  )
}

// --- Position layout and manual purchase prices (task 9) ---

/**
 * What the Gebinde / Anzahl / Inhalt fields of a position show (2026-10-03 layout):
 *
 * - `package`: a multi-piece unit from the Lieferschein — "Karton", 2, 14 Stk.
 * - `unknown`: the import could not resolve the unit and took it as one piece (factor 1); the
 *   quantity may be off by the package factor, so it is flagged until a human checks it.
 * - `base`: everything else — a hand-announced position, or a delivered unit that IS one piece.
 *   The supplier's packing name ("Duni "02" / 1030ml (KG)") is deliberately not shown then.
 */
export type PackageCells =
  | { kind: 'package'; name: string; count: number; content: number }
  | { kind: 'unknown'; count: number }
  | { kind: 'base'; count: number }

export function packageCells(line: CheckLine): PackageCells {
  if (hasUnitWarning(line)) {
    return { kind: 'unknown', count: line.unitCount ?? line.expectedQty }
  }
  const content = line.contentPerUnit ?? 1
  if (line.unitCount != null && content > 1) {
    return {
      kind: 'package',
      name: line.unitName ?? '',
      count: line.unitCount,
      content,
    }
  }
  return { kind: 'base', count: line.expectedQty }
}

export type PriceParseResult =
  | { ok: true; cents: number | null }
  | { ok: false }

/** A typed net price per piece: '' is "no price" (allowed), anything else must be a euro amount. */
export function parsePriceInput(value: string): PriceParseResult {
  if (value.trim() === '') return { ok: true, cents: null }
  const parsed = parseEuroToCents(value)
  return parsed.ok ? { ok: true, cents: parsed.cents } : { ok: false }
}

/** Cents as the price input shows them: "2,70", de-AT decimal comma, no currency sign. */
export function formatPriceInput(cents: number | null | undefined): string {
  if (cents == null) return ''
  return (cents / 100).toFixed(2).replace('.', ',')
}

/**
 * A server line made editable for its price (hand-announced delivery): a stored per-piece price
 * becomes the input's text, no price an empty field.
 */
export function withPriceInput(line: CheckLine): CheckLine {
  const content = line.contentPerUnit ?? 1
  const cents =
    line.unitCostCents != null && content > 0
      ? Math.round(line.unitCostCents / content)
      : null
  return { ...line, price: formatPriceInput(cents) }
}

/**
 * "Alle letzten Preise übernehmen": fills only the EMPTY price fields that have a last price, so a
 * price somebody already typed is never overwritten.
 */
export function fillEmptyPrices<T extends { price?: string }>(
  items: Array<T>,
  lastPriceCents: (item: T) => number | null | undefined,
): Array<T> {
  return items.map((item) => {
    if (item.price === undefined || item.price.trim() !== '') return item
    const last = lastPriceCents(item)
    return last == null ? item : { ...item, price: formatPriceInput(last) }
  })
}

/**
 * The announce form's "Warenwert netto (Positionen mit Preis)": quantity × price of every position
 * that has both readable; positions without a price are left out rather than counted as zero.
 */
export function announcedValueCents(
  lines: Array<{ qty: string; price: string }>,
): number | null {
  let total: number | null = null
  for (const line of lines) {
    const price = parsePriceInput(line.price)
    if (!price.ok || price.cents == null) continue
    const qty = parseStockAmount(line.qty)
    if (!qty.ok) continue
    total = (total ?? 0) + Math.round(price.cents * qty.value)
  }
  return total
}

/**
 * True when the delivery was created by an API client rather than a person — the import
 * endpoint records `api-client:<id>` as the actor (CHG-2026-09-webbill-delivery-import).
 */
export function isApiClientActor(
  createdBy: string | null | undefined,
): boolean {
  return createdBy?.startsWith('api-client:') ?? false
}
